/**
 * G2.27 — IndexedDB-at-rest encryption primitives for the POS PWA.
 *
 * Algorithm: AES-GCM-256 with a 96-bit random IV per record.
 * Key material: a non-exportable `CryptoKey` derived from the per-device KEK
 * that the backend issues via `POST /auth/device/enroll`. The raw KEK
 * leaves the browser only once (the TLS-protected response), is imported
 * immediately as a non-extractable key, and held in module-scoped memory.
 *
 * Rotation strategy: purge-and-resync. `clearKey()` zeros the in-memory key;
 * local IndexedDB must be wiped by the caller immediately after because the
 * ciphertext is now unrecoverable.
 *
 * This module is intentionally self-contained — no Dexie imports — so it
 * can be unit-tested in isolation. PR 2 wires it into the Dexie hooks.
 */

/**
 * Shape written to IndexedDB for an encrypted record payload.
 * - `__enc` is a version tag so migration code can distinguish
 *   still-plaintext legacy rows from encrypted ones.
 * - `iv` and `ct` are `Uint8Array`s because Dexie serializes them
 *   natively without a JSON round-trip (avoiding base64 overhead).
 */
export interface EncryptedPayload {
  __enc: 1;
  iv: Uint8Array;
  ct: Uint8Array;
}

const IV_BYTE_LENGTH = 12; // 96 bits — NIST-recommended for AES-GCM
const KEK_BYTE_LENGTH = 32; // 256-bit AES-GCM

let activeKey: CryptoKey | null = null;

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      'WebCrypto SubtleCrypto unavailable. IndexedDB encryption requires a secure context (https).',
    );
  }
  return subtle;
}

function decodeBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Import the backend-issued KEK as a non-extractable AES-GCM key.
 *
 * The KEK arrives as a base64 string. We decode, import via WebCrypto
 * with `extractable: false`, and immediately zero the intermediate
 * Uint8Array. After this call the only reference to the key material
 * is the opaque `CryptoKey` handle — even a compromised render process
 * cannot read the bytes back.
 */
export async function importDeviceKey(kekBase64: string): Promise<CryptoKey> {
  const raw = decodeBase64(kekBase64);
  if (raw.length !== KEK_BYTE_LENGTH) {
    // Zero before throwing so the wrong-length material doesn't linger on the stack.
    raw.fill(0);
    throw new Error(`KEK must be ${KEK_BYTE_LENGTH} bytes; got ${raw.length}`);
  }
  const key = await getSubtle().importKey(
    'raw',
    raw.buffer.slice(0, raw.byteLength) as ArrayBuffer,
    { name: 'AES-GCM' },
    false, // extractable: false — cannot be read back out
    ['encrypt', 'decrypt'],
  );
  raw.fill(0);
  return key;
}

/** Install the active encryption key. Pass `null` to clear. */
export function setActiveKey(key: CryptoKey | null): void {
  activeKey = key;
}

/** True iff a key is currently installed. */
export function hasActiveKey(): boolean {
  return activeKey !== null;
}

/** Clear the in-memory key. Caller MUST purge IndexedDB next. */
export function clearKey(): void {
  activeKey = null;
}

function requireKey(): CryptoKey {
  if (!activeKey) {
    throw new Error(
      'No active encryption key. Call setActiveKey(importDeviceKey(kek)) first.',
    );
  }
  return activeKey;
}

/**
 * Encrypt an arbitrary JSON-serialisable value. Returns a Dexie-ready blob.
 * Uses a fresh 96-bit random IV per call; callers MUST NOT reuse or stash IVs.
 */
export async function encryptRecord(plain: unknown): Promise<EncryptedPayload> {
  const key = requireKey();
  const json = JSON.stringify(plain);
  // Fresh ArrayBuffer-backed Uint8Arrays — `Uint8Array<ArrayBuffer>` satisfies
  // the strict `BufferSource` constraint that excludes SharedArrayBuffer views.
  const plaintext = new Uint8Array(new TextEncoder().encode(json));
  const iv = new Uint8Array(new ArrayBuffer(IV_BYTE_LENGTH));
  globalThis.crypto.getRandomValues(iv);
  const ctBuf = await getSubtle().encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { __enc: 1, iv, ct: new Uint8Array(ctBuf) };
}

/**
 * Decrypt a blob produced by `encryptRecord`. Throws on:
 * - tampered ciphertext (AES-GCM auth tag mismatch)
 * - key mismatch (wrong device KEK)
 * - malformed blob (missing `__enc`, wrong IV length)
 *
 * The generic `T` is convenience only — the caller is responsible for
 * validating the shape, as always with JSON parse.
 */
export async function decryptRecord<T = unknown>(blob: EncryptedPayload): Promise<T> {
  const key = requireKey();
  if (blob?.__enc !== 1) {
    throw new Error('Not an encrypted payload (missing __enc tag)');
  }
  // Realm-agnostic Uint8Array check: `instanceof` fails when the blob came
  // back through structuredClone from a different realm (jsdom + fake-IDB,
  // or a different document in real browsers). Object.prototype.toString
  // gives us the internal tag regardless of realm.
  const ivTag = Object.prototype.toString.call(blob.iv);
  if (ivTag !== '[object Uint8Array]' || blob.iv.length !== IV_BYTE_LENGTH) {
    throw new Error(`Invalid IV: expected ${IV_BYTE_LENGTH}-byte Uint8Array`);
  }
  // Copy into fresh ArrayBuffer-backed Uint8Arrays so the BufferSource types
  // narrow cleanly under strict TS (SharedArrayBuffer exclusion).
  const iv = new Uint8Array(blob.iv);
  const ct = new Uint8Array(blob.ct);
  const ptBuf = await getSubtle().decrypt(
    { name: 'AES-GCM', iv },
    key,
    ct,
  );
  const json = new TextDecoder().decode(ptBuf);
  return JSON.parse(json) as T;
}

/**
 * Narrow a Dexie-read row to detect whether it was stored encrypted.
 *
 * Structured-clone round-trips through IndexedDB may produce Uint8Arrays
 * from a different realm (common in jsdom + fake-indexeddb, and also in
 * real browsers when the DB was opened in a different document). We use
 * `Object.prototype.toString.call` rather than `instanceof` to make the
 * check realm-agnostic.
 */
export function isEncryptedPayload(value: unknown): value is EncryptedPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as EncryptedPayload;
  if (v.__enc !== 1) return false;
  const ivTag = Object.prototype.toString.call(v.iv);
  const ctTag = Object.prototype.toString.call(v.ct);
  return ivTag === '[object Uint8Array]' && ctTag === '[object Uint8Array]';
}
